import { createChunks, isNonEmptyArray } from '@shared/utils/array';
import { toQueryParams } from '@shared/utils/url';
import { flatten } from 'lodash-es';
import { v1, v2 } from '@/api/client';
import { fetchLatestSamples } from '../samples/api';

type EquipmentBaseIncludes =
  | 'alarm_count'
  | 'com_device'
  | 'documents'
  | 'inspection_interval'
  | 'presentations'
  | 'product_image'
  | 'trip_meter';

export type EquipmentIncludeV1 = (
  | EquipmentBaseIncludes
  | 'load'
  | 'product_model_3d'
  | 'sensor_count'
  | 'rul'
)[];

export type EquipmentIncludeV2 = (EquipmentBaseIncludes | 'relations')[];

export const fetchEquipmentByEntityIds = (
  ids?: string[],
  include?: EquipmentIncludeV2
) => {
  if (!isNonEmptyArray(ids)) {
    return Promise.resolve([]);
  }

  const chunks = createChunks(ids, 10);

  return Promise.all(
    chunks.map((chunk) =>
      v2
        .getPaginated<Equipment>(
          'equipment',
          {
            entity_ids: chunk.join(','),
            include,
            page_size: 1000,
          },
          {},
          'page_number'
        )
        .then(flatten)
    )
  ).then((result) => flatten(result));
};

export const fetchEquipmentByIds = (
  equipmentIds?: string[],
  include?: EquipmentIncludeV1
) => {
  if (!isNonEmptyArray(equipmentIds)) {
    return Promise.resolve([]);
  }

  const chunks = createChunks(equipmentIds, 100);

  return Promise.all(
    chunks.map((chunk) => {
      const query = toQueryParams({ include, equipment_ids: chunk });
      return v1.get<Equipment[]>(`equipment${query}`);
    })
  ).then((result) => flatten(result));
};

export const fetchEquipmentSignalOffset = (equipmentId?: string) => {
  if (!equipmentId) {
    return Promise.resolve([]);
  }
  return v1.get<SignalOffset[]>(`equipment/${equipmentId}/signaloffset`);
};

export const addEquipmentSignalOffset = (offset: Partial<SignalOffset>) => {
  if (!offset.equipmentId) {
    return Promise.resolve();
  }

  const postOffset = (offset: Partial<SignalOffset>) =>
    v1.post(`equipment/${offset.equipmentId}/signaloffset`, offset);

  if (offset.createdAt) {
    return postOffset(offset);
  }

  return fetchLatestSamples([offset.equipmentId], {
    signal_names: [offset.signalName],
    max_age: 'P1D',
  }).then((samples) => {
    const sample = samples[0];

    return postOffset({
      createdAt: sample && sample.timeStamp,
      ...offset,
    });
  });
};

export const resetTripMeter = (equipmentId?: string) => {
  if (!equipmentId) {
    return Promise.resolve();
  }
  return v1.post(`equipment/${equipmentId}/tripmeter`, undefined);
};

export const fetchReportingTime = (equipmentId?: string) => {
  if (!equipmentId) {
    return Promise.resolve(undefined);
  }
  return v1.get<ReportingTime>(`equipment/${equipmentId}/reporting`);
};

export const updateReportingTime = (
  equipmentId?: string,
  reportingTime?: Partial<ReportingTime>
) => {
  if (!equipmentId) {
    return Promise.resolve();
  }
  return v1.put(`equipment/${equipmentId}/reporting`, reportingTime);
};

export const fetchEquipmentPermitsMe = (equipmentId: string | undefined) => {
  if (!equipmentId) {
    return Promise.resolve([]);
  }
  return v2.get<Permit[]>(`equipment/${equipmentId}/permit/me`);
};

export const fetchExternalEquipment = (serial?: string) => {
  if (!serial) {
    return Promise.resolve(undefined);
  }
  return v1.get<EquipmentEntity>(
    `external/type/atd/equipment?serialNumber=${serial}`
  );
};
